import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// Types
export interface ProductWarehouse {
  id: number;
  product_id: number;
  warehouse_id: number;
  available_quantity: number;
  created_at?: string;
  updated_at?: string;
  // Additional fields from API response
  product_name?: string;
  product_short_name?: string;
  product_main_image?: string;
  warehouse_name?: string;
  product_price?: number;
  currency?: string;
}

export interface ProductWarehouseListParams {
  page?: number;
  limit?: number;
  product_id?: number;
  warehouse_id?: number;
  vendor_id?: number;
}

export interface AddProductWarehouseRequest {
  product_id: number;
  warehouse_id: number;
  available_quantity: number;
}

export interface UpdateProductWarehouseRequest {
  id: number;
  product_id: number;
  warehouse_id: number;
  available_quantity: number;
}

export interface DeleteProductWarehouseRequest {
  id: number;
  product_id?: number; // Added for verification
}

export interface ApiResponse<T = any> {
  error: boolean;
  message: string;
  result?: T;
  data?: T;
  warehouses?: T;
  count?: number;
  total?: number;
  id?: number;
}

// API Configuration
export const API_CONFIG = {
  BASE_URL: 'https://fftestservice.sarsetech.com',
  AUTH_KEY: '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3',
  ENDPOINTS: {
    LIST_PRODUCT_WAREHOUSES: '/product/listproductwarehouses',
    ADD_PRODUCT_WAREHOUSE: '/product/addproductwarehouse',
    UPDATE_PRODUCT_WAREHOUSE: '/product/updateproductwarehouse',
    DELETE_PRODUCT_WAREHOUSE: '/product/deleteproductwarehouse',
  },
};

// Product Warehouse API
export const productWarehouseApi = createApi({
  reducerPath: 'productWarehouseApi',
  baseQuery: fetchBaseQuery({
    baseUrl: API_CONFIG.BASE_URL,
    prepareHeaders: (headers) => {
      const token = localStorage.getItem('admin_token') || localStorage.getItem('vendor_token');
      const userRole = localStorage.getItem('user_role');

      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }

      headers.set('authkey', API_CONFIG.AUTH_KEY);
      headers.set('Content-Type', 'application/json');

      // set vendor id for vendor users
      if (userRole === 'vendor') {
        const vendorId = localStorage.getItem('vendor_id');
        if (vendorId) {
          headers.set('vendor-id', vendorId);
        }
      }

      return headers;
    },
  }),
  tagTypes: ['ProductWarehouse'],
  endpoints: (build) => ({
    // ✅ CRITICAL FIX: List Product Warehouses with MANDATORY product_id filter and sorting
    listProductWarehouses: build.query<ApiResponse<ProductWarehouse[]>, ProductWarehouseListParams>({
      query: ({ page = 1, limit = 100, product_id, warehouse_id, vendor_id } = {}) => {
        const userRole = localStorage.getItem('user_role');
        const currentVendorId = localStorage.getItem('vendor_id');
        
        const body: any = {
          start: (page - 1) * limit,
          limit,
          // ✅ Sort by updated_at DESC to show recently updated products first
          sortby: 'updated_at',
          sortorder: 'desc'
        };

        // ✅ CRITICAL: ALWAYS filter by product_id when provided
        if (product_id) {
          body.product_id = product_id;
          console.log('🔍 Fetching warehouse stocks for Product ID:', product_id);
        }

        if (warehouse_id) {
          body.warehouse_id = warehouse_id;
        }

        // Auto-add vendor filter for vendor users
        if (userRole === 'vendor' && currentVendorId) {
          body.vendor_id = parseInt(currentVendorId);
        } else if (vendor_id) {
          body.vendor_id = vendor_id;
        }

        return {
          url: API_CONFIG.ENDPOINTS.LIST_PRODUCT_WAREHOUSES,
          method: 'POST',
          body: JSON.stringify(body),
        };
      },
      providesTags: (result, error, arg) => {
        const tags: any[] = ['ProductWarehouse'];
        if (arg.product_id) {
          tags.push({ type: 'ProductWarehouse', id: arg.product_id });
        }
        return tags;
      },
      transformResponse: (response: any, meta, arg) => {
        let stocks = response.warehouses || response.result || response.data || [];
        
        // ✅ CRITICAL: Double-check product_id filtering (extra safety layer)
        if (arg.product_id) {
          stocks = stocks.filter((stock: ProductWarehouse) => 
            stock.product_id === arg.product_id
          );
          console.log(`✅ Filtered stocks for Product ID ${arg.product_id}:`, stocks.length);
        }
        
        // ✅ Sort by updated_at on frontend as well (extra safety)
        stocks.sort((a: ProductWarehouse, b: ProductWarehouse) => {
          const dateA = new Date(a.updated_at || a.created_at || 0).getTime();
          const dateB = new Date(b.updated_at || b.created_at || 0).getTime();
          return dateB - dateA; // DESC order (newest first)
        });
        
        return {
          error: response.error || false,
          message: response.message || '',
          result: stocks,
          count: stocks.length,
        };
      },
    }),

    // ✅ Add Product Warehouse - Check for duplicates
    addProductWarehouse: build.mutation<ApiResponse<any>, AddProductWarehouseRequest>({
      query: (data) => {
        const userRole = localStorage.getItem('user_role');
        const vendorId = localStorage.getItem('vendor_id');

        const body: any = {
          product_id: data.product_id,
          warehouse_id: data.warehouse_id,
          available_quantity: data.available_quantity,
        };

        // Auto-add vendor_id for vendor users
        if (userRole === 'vendor' && vendorId) {
          body.vendor_id = parseInt(vendorId);
        }

        console.log('➕ Adding warehouse stock:', body);

        return {
          url: API_CONFIG.ENDPOINTS.ADD_PRODUCT_WAREHOUSE,
          method: 'POST',
          body: JSON.stringify(body),
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'ProductWarehouse', id: arg.product_id },
        'ProductWarehouse'
      ],
      transformResponse: (response: any, meta, arg) => {
        console.log('✅ Added warehouse stock for Product ID:', arg.product_id);
        return response;
      },
    }),

    // ✅ CRITICAL FIX: Update Product Warehouse - MUST verify product_id
    updateProductWarehouse: build.mutation<ApiResponse<any>, UpdateProductWarehouseRequest>({
      query: (data) => {
        const userRole = localStorage.getItem('user_role');
        const vendorId = localStorage.getItem('vendor_id');

        const body: any = {
          id: data.id,
          product_id: data.product_id, // ✅ CRITICAL: Always send product_id
          warehouse_id: data.warehouse_id,
          available_quantity: data.available_quantity,
        };

        // Auto-add vendor_id for vendor users
        if (userRole === 'vendor' && vendorId) {
          body.vendor_id = parseInt(vendorId);
        }

        console.log('✏️ Updating warehouse stock:', body);

        return {
          url: API_CONFIG.ENDPOINTS.UPDATE_PRODUCT_WAREHOUSE,
          method: 'POST',
          body: JSON.stringify(body),
        };
      },
      invalidatesTags: (result, error, arg) => [
        { type: 'ProductWarehouse', id: arg.product_id },
        'ProductWarehouse'
      ],
      transformResponse: (response: any, meta, arg) => {
        console.log('✅ Updated warehouse stock for Product ID:', arg.product_id);
        return response;
      },
    }),

    // ✅ CRITICAL FIX: Delete Product Warehouse - Verify product ownership
    deleteProductWarehouse: build.mutation<ApiResponse<any>, DeleteProductWarehouseRequest>({
      query: ({ id, product_id }) => {
        const userRole = localStorage.getItem('user_role');
        const vendorId = localStorage.getItem('vendor_id');

        const body: any = { 
          id,
        };

        // Add product_id for verification if provided
        if (product_id) {
          body.product_id = product_id;
        }

        // Add vendor_id for vendor users
        if (userRole === 'vendor' && vendorId) {
          body.vendor_id = parseInt(vendorId);
        }

        console.log('🗑️ Deleting warehouse stock:', body);

        return {
          url: API_CONFIG.ENDPOINTS.DELETE_PRODUCT_WAREHOUSE,
          method: 'POST',
          body: JSON.stringify(body),
        };
      },
      invalidatesTags: (result, error, arg) => {
        const tags: any[] = ['ProductWarehouse'];
        if (arg.product_id) {
          tags.push({ type: 'ProductWarehouse', id: arg.product_id });
        }
        return tags;
      },
      transformResponse: (response: any, meta, arg) => {
        console.log('✅ Deleted warehouse stock:', arg.id);
        return response;
      },
    }),
  }),
});

export const {
  useListProductWarehousesQuery,
  useAddProductWarehouseMutation,
  useUpdateProductWarehouseMutation,
  useDeleteProductWarehouseMutation,
} = productWarehouseApi;